<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import Modal from './Modal.vue';
import { useStorage } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  forceShow: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'update:show']);

// Controle de visibilidade
const showModal = ref(props.show);

// Estado de "já viu o tutorial" salvo no localStorage por conta
const currentAccountId = useMapGetter('getCurrentAccountId');

const tutorialSeenKey = computed(() =>
  `tutorial-seen-${currentAccountId.value}`
);

const hasSeenTutorial = useStorage(tutorialSeenKey.value, false);

// Videos tutoriais - mesmos da tela de login
const tutorialVideos = ref([
  {
    id: 1,
    title: 'Apresentacao da plataforma',
    description: 'Conheca todas as funcionalidades do JUScrm',
    url: null, // Ex: 'https://www.youtube.com/embed/VIDEO_ID'
  },
  {
    id: 2,
    title: 'Configurando a IA',
    description: 'Como usar a inteligencia artificial',
    url: null,
  },
  {
    id: 3,
    title: 'Ferramentas',
    description: 'Todas as ferramentas disponiveis',
    url: null,
  },
]);

// Video selecionado para exibir em destaque
const selectedVideo = ref(tutorialVideos.value[0]);

const selectVideo = (video) => {
  selectedVideo.value = video;
};

const closeModal = () => {
  showModal.value = false;
  // Marcar como visto
  hasSeenTutorial.value = true;
  emit('close');
  emit('update:show', false);
};

// Verificar se deve mostrar automaticamente
const checkAutoShow = () => {
  // Verificar se veio do aceite de contrato
  const urlParams = new URLSearchParams(window.location.search);
  const justAcceptedContract = urlParams.get('contract_accepted') === 'true';

  // Mostrar se: forceShow OU (veio do aceite de contrato E nunca viu)
  if (props.forceShow || (justAcceptedContract && !hasSeenTutorial.value)) {
    showModal.value = true;
    // Limpar o parametro da URL para nao mostrar novamente no refresh
    if (justAcceptedContract) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }
};

// Sincronizar com prop externa
watch(() => props.show, (newVal) => {
  showModal.value = newVal;
});

watch(() => props.forceShow, (newVal) => {
  if (newVal) {
    showModal.value = true;
  }
});

// Handler para evento global de abertura do modal
const handleOpenTutorial = () => {
  showModal.value = true;
};

onMounted(() => {
  checkAutoShow();
  // Escutar evento global para abrir o modal
  emitter.on('OPEN_TUTORIAL_MODAL', handleOpenTutorial);
});

onUnmounted(() => {
  emitter.off('OPEN_TUTORIAL_MODAL', handleOpenTutorial);
});
</script>

<template>
  <Modal
    v-model:show="showModal"
    :close-on-backdrop-click="true"
    :show-close-button="true"
    @close="closeModal"
  >
    <div class="p-6 w-[850px] max-w-full">
      <!-- Header -->
      <div class="text-center mb-4">
        <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Bem-vindo ao JUScrm!
        </h2>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Assista aos videos abaixo para conhecer a plataforma
        </p>
      </div>

      <!-- Area do Video Principal -->
      <div class="mb-4">
        <div class="h-[280px] bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden">
          <!-- Placeholder quando nao tem URL -->
          <div v-if="!selectedVideo?.url" class="w-full h-full flex items-center justify-center text-slate-400">
            <div class="text-center p-6">
              <svg
                class="w-14 h-14 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 class="font-medium text-slate-700 dark:text-slate-300">
                {{ selectedVideo?.title }}
              </h3>
              <p class="text-sm">Video em breve</p>
            </div>
          </div>
          <!-- Iframe do video quando disponivel -->
          <iframe
            v-else
            :src="selectedVideo?.url"
            class="w-full h-full"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
        </div>
        <div class="mt-2">
          <h3 class="font-semibold text-base text-slate-900 dark:text-slate-100">
            {{ selectedVideo?.title }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ selectedVideo?.description }}
          </p>
        </div>
      </div>

      <!-- Lista de Videos -->
      <div class="border-t border-slate-200 dark:border-slate-700 pt-4">
        <h4 class="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
          Todos os videos
        </h4>
        <div class="grid grid-cols-3 gap-3">
          <button
            v-for="video in tutorialVideos"
            :key="video.id"
            class="text-left rounded-lg overflow-hidden transition-all border-2"
            :class="[
              selectedVideo?.id === video.id
                ? 'border-woot-500 ring-2 ring-woot-500/20'
                : 'border-transparent hover:border-slate-200 dark:hover:border-slate-600'
            ]"
            @click="selectVideo(video)"
          >
            <!-- Thumbnail -->
            <div class="h-16 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <div v-if="!video.url" class="text-slate-400">
                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <!-- Info -->
            <div class="px-2 py-2 h-10 flex items-center justify-center">
              <p class="text-xs font-medium text-slate-700 dark:text-slate-300 text-center leading-tight">
                {{ video.title }}
              </p>
            </div>
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-5 flex justify-between items-center">
        <a
          href="https://wa.me/5518997298952"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 px-5 py-2 bg-woot-500 hover:bg-woot-600 !text-white text-sm font-medium rounded-lg transition-colors"
        >
          <svg class="w-5 h-5" fill="white" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Suporte
        </a>
        <button
          class="px-5 py-2 bg-woot-500 hover:bg-woot-600 text-white text-sm font-medium rounded-lg transition-colors"
          @click="closeModal"
        >
          Comecar a usar
        </button>
      </div>
    </div>
  </Modal>
</template>
